import React, { useState, useEffect } from 'react';
import { StepHeader } from '../ui/StepHeader.jsx';
import { StepIntro } from '../ui/StepIntro.jsx';
import { Icon } from '../ui/Icon.jsx';
import { centsToReais, maskDocument, isValidDocument } from '../utils/masks.js';
import { getVocabulary, frequencyAdjective } from '../utils/vocabulary.js';
import { detectBrandCandidates, brandLabel } from '../utils/cardBrand.js';
import { FormError } from '../ui/FormError.jsx';
import { CardBrandIcon } from '../ui/CardBrandIcon.jsx';
import { IS_DEV } from '../utils/env.js';
import { loadCardCrypto } from '../utils/lazyModules.js';
import { validateCard, validateCardField, maskCardNumber, cardNumberPlaceholder, securityCodeSize } from '../utils/cardValidation.js';
import { calculateProcessingCost, findProcessingCostRate, resolveChargedAmount } from '../utils/processingCost.js';

const CARD_NUMBER_MAX_LENGTH = 23;
// Os primeiros digitos casam com varias bandeiras ao mesmo tempo; abaixo disso
// perguntar a bandeira e ruido, o proprio numero ainda vai desambiguar.
const MIN_DIGITS_TO_CONFIRM_BRAND = 3;

const PAYMENT_META = {
  CreditCard:   { label: 'Cartão de crédito',  icon: 'card',   desc: 'Confirmação imediata' },
  Pix:          { label: 'Pix',                icon: 'pix',    desc: 'Pague pelo app do banco' },
  PixAutomatic: { label: 'Pix Automático',     icon: 'pix',    desc: 'Autorize uma vez no app do banco' },
  Boleto:       { label: 'Boleto',             icon: 'boleto', desc: 'Compensa em até 3 dias úteis' },
  DebitAccount: { label: 'Débito em conta',    icon: 'bank',   desc: 'Débito automático na conta' }
};

// value must match the backend CardBrandEnum member name (bound by name).
// Used only as a fallback when auto-detection can't identify the brand.
const CARD_BRANDS = [
  { value: 'Visa',            label: 'Visa' },
  { value: 'MasterCard',      label: 'Mastercard' },
  { value: 'Elo',             label: 'Elo' },
  { value: 'AmericanExpress', label: 'American Express' },
  { value: 'Hipercard',       label: 'Hipercard' },
  { value: 'DinersClub',      label: 'Diners Club' },
  { value: 'Maestro',         label: 'Maestro' }
];

// value must match the backend GatewayEnum member name (debit-capable banks).
const DEBIT_BANKS = [
  { value: 'BancoDoBrasil',  label: 'Banco do Brasil' },
  { value: 'CaixaEconomica', label: 'Caixa Econômica' },
  { value: 'Bradesco',       label: 'Bradesco' },
  { value: 'Itau',           label: 'Itaú' },
  { value: 'Santander',      label: 'Santander' },
  { value: 'Banrisul',       label: 'Banrisul' }
];

// value must match the backend BankAccountOperationTypeEnum member name.
const OPERATION_TYPES = [
  { value: 'CheckingAccountIndividual', label: 'Conta corrente (PF)' },
  { value: 'SavingsIndividual',         label: 'Poupança (PF)' },
  { value: 'CheckingAccountBusiness',   label: 'Conta corrente (PJ)' },
  { value: 'SavingsBusiness',           label: 'Poupança (PJ)' }
];

// Dev fill datasets — a random group is picked per click.
const FAKE_CARDS = [
  { cardNumber: '4000 0000 0000 0010', cardBrand: 'Visa',       cardName: 'MARIA SILVA',   expiry: '12/30', cvv: '123' },
  { cardNumber: '4000 0000 0000 0010', cardBrand: 'Visa',       cardName: 'JOAO SOUZA',    expiry: '05/29', cvv: '456' },
  { cardNumber: '5555 5555 5555 4444', cardBrand: 'MasterCard', cardName: 'ANA OLIVEIRA',  expiry: '08/28', cvv: '789' },
  { cardNumber: '4000 0000 0000 0010', cardBrand: 'Visa',       cardName: 'PEDRO SANTOS',  expiry: '03/31', cvv: '321' },
  { cardNumber: '5555 5555 5555 4444', cardBrand: 'MasterCard', cardName: 'CARLA LIMA',    expiry: '11/27', cvv: '654' },
  { cardNumber: '4000 0000 0000 0010', cardBrand: 'Visa',       cardName: 'LUCAS PEREIRA', expiry: '07/30', cvv: '987' }
];
const FAKE_DEBITS = [
  { gateway: 'Banrisul', ownerName: 'Maria Silva',   ownerDocument: '391.916.523-32', agency: '1234', agencyDigit: '1', accountNumber: '56789', accountDigit: '0', operationType: 'CheckingAccountIndividual' },
  { gateway: 'Banrisul', ownerName: 'João Souza',    ownerDocument: '285.471.890-92', agency: '4321', agencyDigit: '2', accountNumber: '98765', accountDigit: '1', operationType: 'SavingsIndividual' },
  { gateway: 'Banrisul', ownerName: 'Ana Oliveira',  ownerDocument: '123.654.987-28', agency: '5678', agencyDigit: '3', accountNumber: '11223', accountDigit: '4', operationType: 'CheckingAccountIndividual' },
  { gateway: 'Banrisul', ownerName: 'Pedro Santos',  ownerDocument: '456.789.123-64', agency: '8765', agencyDigit: '4', accountNumber: '44556', accountDigit: '7', operationType: 'SavingsIndividual' }
];

/**
 * Payment step — vertical method list, inline fields, total row.
 */
export function PaymentStep({ config, formData, onChange, onBack, onClose, onSubmit, isSubmitting, isInline, isPreview, canGoBack }) {
  const { Frequencies } = config.Behaviour;
  const [paymentErrors, setPaymentErrors] = useState({});

  const selectedFrequency = Frequencies.find(f => f.Id === formData.frequencyId) || Frequencies[0];
  const hasRefusedCharge = Boolean(formData.chargeResult?.status);
  const availableMethods  = (selectedFrequency?.PaymentMethods || [])
    .filter(method => !(hasRefusedCharge && method === 'PixAutomatic'));

  // Reset payment method if it's no longer valid for the current frequency
  useEffect(() => {
    if (formData.paymentMethod === 'Pix' && availableMethods.includes('PixAutomatic')) {
      onChange({ paymentMethod: 'PixAutomatic', paymentDetails: {} });
    } else if (formData.paymentMethod && !availableMethods.includes(formData.paymentMethod)) {
      onChange({ paymentMethod: availableMethods[0] || null, paymentDetails: {} });
    } else if (!formData.paymentMethod && availableMethods.length > 0) {
      onChange({ paymentMethod: availableMethods[0], paymentDetails: {} });
    }
  }, [formData.frequencyId]);

  useEffect(() => {
    if (formData.paymentMethod === 'CreditCard' && !isPreview) loadCardCrypto().catch(() => {});
  }, [formData.paymentMethod, isPreview]);

  function handleMethodSelect(method) {
    setPaymentErrors({});
    if (method === 'DebitAccount') {
      const donor = formData.donorInfo || {};
      onChange({
        paymentMethod: method,
        paymentDetails: {
          ownerName: donor.Name || '',
          ownerDocument: donor.Document || '',
          operationType: 'CheckingAccountIndividual'
        }
      });
      return;
    }
    onChange({ paymentMethod: method, paymentDetails: {} });
  }

  function handleDetailChange(key, value) {
    onChange({ paymentDetails: { ...formData.paymentDetails, [key]: value } });
    clearFieldErrors([key]);
  }

  function handleDetailsMerge(partial) {
    onChange({ paymentDetails: { ...formData.paymentDetails, ...partial } });
    clearFieldErrors(Object.keys(partial));
  }

  function clearFieldErrors(keys) {
    setPaymentErrors(prev => {
      if (!keys.some(key => prev[key])) return prev;
      const next = { ...prev };
      for (const key of keys) delete next[key];
      return next;
    });
  }

  function handleFieldBlur(key) {
    const error = validatePaymentField(formData.paymentMethod, key, formData.paymentDetails || {});
    setPaymentErrors(prev => ({ ...prev, [key]: error || undefined }));
  }

  function handleSubmit() {
    if (!isPreview) {
      const errors = validatePaymentDetails(formData.paymentMethod, formData.paymentDetails || {});
      if (Object.keys(errors).length > 0) {
        setPaymentErrors(errors);
        return;
      }
    }
    onSubmit();
  }

  const canProceed = isPaymentValid(formData.paymentMethod, formData.paymentDetails || {});

  function fillFake() {
    const details = formData.paymentDetails || {};
    setPaymentErrors({});
    if (formData.paymentMethod === 'CreditCard') {
      const card = FAKE_CARDS[Math.floor(Math.random() * FAKE_CARDS.length)];
      onChange({ paymentDetails: { ...details, ...card } });
    } else if (formData.paymentMethod === 'DebitAccount') {
      const donor = formData.donorInfo || {};
      const debit = FAKE_DEBITS[Math.floor(Math.random() * FAKE_DEBITS.length)];
      onChange({
        paymentDetails: {
          ...details, ...debit,
          ownerName: debit.ownerName || donor.Name || 'Maria Teste',
          ownerDocument: debit.ownerDocument || donor.Document || '529.982.247-25'
        }
      });
    }
  }

  const amount     = formData.amount || 0;
  const amountText = centsToReais(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const chargedAmountText = centsToReais(resolveChargedAmount(config, formData), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const processingCostRate = findProcessingCostRate(config, formData.paymentMethod);
  const processingCostAmount = processingCostRate ? calculateProcessingCost(processingCostRate, Math.round(amount)) : null;
  const vocab = getVocabulary(config);
  const freqAdjective = selectedFrequency ? frequencyAdjective(selectedFrequency.Id, vocab) : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <StepHeader
        onClose={onClose}
        showClose={!isInline}
        onDevFill={IS_DEV && !isPreview ? fillFake : undefined}
      />

      <div className="tdw-step-body">
        <StepIntro
          title="Como você prefere pagar?"
          subtitle={(
            <>
              <span className="tdw-money">R$ {amountText}</span>
              {freqAdjective && <> · {vocab.term} {freqAdjective}</>}
            </>
          )}
        />

        {/* Payment method list */}
        <div className="tdw-payment-list">
          {availableMethods.map(method => {
            const meta = PAYMENT_META[method] || { label: method, icon: '💰' };
            return (
              <button
                key={method}
                type="button"
                className={`tdw-payment-row${formData.paymentMethod === method ? ' selected' : ''}`}
                onClick={() => handleMethodSelect(method)}
              >
                <span className="tdw-payment-row-head">
                  <span className="tdw-payment-row-icon"><Icon name={meta.icon} size={20} /></span>
                  {meta.label}
                </span>
                {meta.desc && <span className="tdw-payment-row-desc">{meta.desc}</span>}
              </button>
            );
          })}
        </div>

        {/* Method panels */}
        {formData.paymentMethod === 'CreditCard' && (
          <div className="tdw-payment-panel">
            <CreditCardPanel
              details={formData.paymentDetails || {}}
              errors={paymentErrors}
              onChange={handleDetailChange}
              onChangeMany={handleDetailsMerge}
              onBlurField={handleFieldBlur}
            />
          </div>
        )}
        {formData.paymentMethod === 'Pix' && (
          <div className="tdw-payment-panel">
            <PaymentNotice text="O QR Code Pix será gerado após a confirmação dos dados." />
          </div>
        )}
        {formData.paymentMethod === 'PixAutomatic' && (
          <div className="tdw-payment-panel">
            <PaymentNotice text="O QR Code será gerado após a confirmação dos dados. Ao pagar, você autoriza no seu banco a cobrança automática deste valor todo mês." />
          </div>
        )}
        {formData.paymentMethod === 'Boleto' && (
          <div className="tdw-payment-panel">
            <PaymentNotice text="O boleto será gerado após a confirmação do endereço." />
          </div>
        )}
        {formData.paymentMethod === 'DebitAccount' && (
          <div className="tdw-payment-panel">
            <DebitPanel
              details={formData.paymentDetails || {}}
              errors={paymentErrors}
              onChange={handleDetailChange}
              onChangeMany={handleDetailsMerge}
              onBlurField={handleFieldBlur}
            />
          </div>
        )}

        {processingCostAmount !== null && (
          <label className="tdw-checkbox-row tdw-processing-cost-row">
            <input
              type="checkbox"
              checked={formData.processingCostAccepted === true}
              onChange={e => onChange({ processingCostAccepted: e.target.checked })}
            />
            <span>
              {config.Behaviour.ProcessingCost.Label}{' '}
              (+ <span className="tdw-money">R$ {centsToReais(processingCostAmount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>)
            </span>
          </label>
        )}

        <div style={{ flex: 1 }} />

        <FormError message={formData.apiError} />

        <button
          type="button"
          className="tdw-btn-cta"
          onClick={handleSubmit}
          disabled={!isPreview && (!canProceed || isSubmitting)}
        >
          {isSubmitting
            ? 'Aguarde...'
            : <>Confirmar {vocab.term} de <span className="tdw-money">R$ {chargedAmountText}</span></>}
        </button>

        <p className="tdw-secure-note">
          <Icon name="lock" size={14} />
          Pagamento processado em ambiente seguro
        </p>

        {canGoBack && (
          <button
            type="button"
            className="tdw-btn-link"
            onClick={onBack}
            disabled={isSubmitting}
          >
            ← Voltar
          </button>
        )}
      </div>
    </div>
  );
}

/* ---- Sub-panels ---- */

function CreditCardPanel({ details, errors, onChange, onChangeMany, onBlurField }) {
  const cardNumber = details.cardNumber || '';
  const digits = cardNumber.replace(/\D/g, '');
  const candidates = detectBrandCandidates(cardNumber);
  const showChooser  = digits.length >= MIN_DIGITS_TO_CONFIRM_BRAND && candidates.length > 1; // e.g. Elo vs Maestro
  const showFallback = candidates.length === 0 && digits.length >= 6; // unknown brand
  const showChip     = Boolean(details.cardBrand) && candidates.length <= 1;
  const numberError  = errors.cardNumber || (!showChooser && !showFallback ? errors.cardBrand : null);

  // Auto-detect the brand from the number: lock it in once unambiguous, keep the
  // current choice if it's still a candidate, clear it when nothing matches.
  function handleNumberChange(raw) {
    const next = maskCardNumber(raw);
    const matches = detectBrandCandidates(next);
    let cardBrand = details.cardBrand || '';
    if (matches.length === 1) cardBrand = matches[0];
    else if (matches.length > 1) cardBrand = matches.includes(cardBrand) ? cardBrand : matches[0];
    else cardBrand = '';
    onChangeMany({ cardNumber: next, cardBrand });
  }

  return (
    <div>
      <div className="tdw-field">
        <label className="tdw-label">Número do cartão</label>
        <div className="tdw-card-number-wrap">
          <input
            type="text"
            className={`tdw-input${numberError ? ' error' : ''}`}
            placeholder={cardNumberPlaceholder(cardNumber)}
            maxLength={CARD_NUMBER_MAX_LENGTH}
            value={cardNumber}
            onChange={e => handleNumberChange(e.target.value)}
            onBlur={() => onBlurField('cardNumber')}
            autoComplete="cc-number"
            inputMode="numeric"
          />
          {showChip && <CardBrandIcon brand={details.cardBrand} className="tdw-card-brand-chip" />}
        </div>
        {numberError && <p className="tdw-field-error">{numberError}</p>}
      </div>

      {showChooser && (
        <div className="tdw-field">
          <label className="tdw-label">Confirme a bandeira</label>
          <div className="tdw-brand-chooser">
            {candidates.map(b => (
              <button
                key={b}
                type="button"
                className={`tdw-brand-option${details.cardBrand === b ? ' selected' : ''}`}
                onClick={() => onChange('cardBrand', b)}
              >
                <CardBrandIcon brand={b} width={28} />
                {brandLabel(b)}
              </button>
            ))}
          </div>
          {errors.cardBrand && <p className="tdw-field-error">{errors.cardBrand}</p>}
        </div>
      )}

      {showFallback && (
        <div className="tdw-field">
          <label className="tdw-label">Bandeira</label>
          <div className="tdw-select-wrap">
            <select
              className={`tdw-input${errors.cardBrand ? ' error' : ''}`}
              value={details.cardBrand || ''}
              onChange={e => onChange('cardBrand', e.target.value)}
              onBlur={() => onBlurField('cardBrand')}
            >
              <option value="">Selecionar bandeira</option>
              {CARD_BRANDS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
            <span className="tdw-select-arrow"><Icon name="chevronDown" size={16} /></span>
          </div>
          {errors.cardBrand && <p className="tdw-field-error">{errors.cardBrand}</p>}
        </div>
      )}

      <div className="tdw-field">
        <label className="tdw-label">Nome no cartão</label>
        <input
          type="text"
          className={`tdw-input${errors.cardName ? ' error' : ''}`}
          placeholder="Como aparece no cartão"
          value={details.cardName || ''}
          onChange={e => onChange('cardName', e.target.value.toUpperCase())}
          onBlur={() => onBlurField('cardName')}
          autoComplete="cc-name"
        />
        {errors.cardName && <p className="tdw-field-error">{errors.cardName}</p>}
      </div>
      <div className="tdw-field-row">
        <div className="tdw-field">
          <label className="tdw-label">Validade</label>
          <input
            type="text"
            className={`tdw-input${errors.expiry ? ' error' : ''}`}
            placeholder="MM/AA"
            maxLength={5}
            value={details.expiry || ''}
            onChange={e => onChange('expiry', formatExpiry(e.target.value))}
            onBlur={() => onBlurField('expiry')}
            autoComplete="cc-exp"
            inputMode="numeric"
          />
          {errors.expiry && <p className="tdw-field-error">{errors.expiry}</p>}
        </div>
        <div className="tdw-field">
          <label className="tdw-label">CVV</label>
          <input
            type="text"
            className={`tdw-input${errors.cvv ? ' error' : ''}`}
            placeholder={'0'.repeat(securityCodeSize(cardNumber))}
            maxLength={securityCodeSize(cardNumber)}
            value={details.cvv || ''}
            onChange={e => onChange('cvv', e.target.value.replace(/\D/g, ''))}
            onBlur={() => onBlurField('cvv')}
            autoComplete="cc-csc"
            inputMode="numeric"
          />
          {errors.cvv && <p className="tdw-field-error">{errors.cvv}</p>}
        </div>
      </div>
    </div>
  );
}

function PaymentNotice({ text }) {
  return (
    <div className="tdw-payment-notice">
      {text}
    </div>
  );
}

function DebitPanel({ details, errors, onChange, onChangeMany, onBlurField }) {
  return (
    <div>
      <div className="tdw-field">
        <label className="tdw-label">Banco</label>
        <div className="tdw-select-wrap">
          <select
            className="tdw-input"
            value={details.gateway || ''}
            onChange={e => onChange('gateway', e.target.value)}
          >
            <option value="">Selecionar banco</option>
            {DEBIT_BANKS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
          <span className="tdw-select-arrow"><Icon name="chevronDown" size={16} /></span>
        </div>
      </div>
      <div className="tdw-field">
        <label className="tdw-label">Titular da conta</label>
        <input
          type="text"
          className="tdw-input"
          placeholder="Nome do titular"
          value={details.ownerName || ''}
          onChange={e => onChange('ownerName', e.target.value)}
        />
      </div>
      <div className="tdw-field">
        <label className="tdw-label">CPF/CNPJ do titular</label>
        <input
          type="text"
          className={`tdw-input${errors.ownerDocument ? ' error' : ''}`}
          placeholder="CPF ou CNPJ"
          inputMode="numeric"
          maxLength={18}
          value={details.ownerDocument || ''}
          onChange={e => onChange('ownerDocument', maskDocument(e.target.value))}
          onBlur={() => onBlurField('ownerDocument')}
        />
        {errors.ownerDocument && <p className="tdw-field-error">{errors.ownerDocument}</p>}
      </div>
      <div className="tdw-field-row">
        <div className="tdw-field">
          <label className="tdw-label">Agência</label>
          <input
            type="text"
            className="tdw-input"
            placeholder="0000-0"
            inputMode="numeric"
            value={joinDv(details.agency, details.agencyDigit)}
            onChange={e => {
              const { number, digit } = splitDv(e.target.value);
              onChangeMany({ agency: number, agencyDigit: digit });
            }}
          />
        </div>
        <div className="tdw-field">
          <label className="tdw-label">Conta</label>
          <input
            type="text"
            className="tdw-input"
            placeholder="00000-0"
            inputMode="numeric"
            value={joinDv(details.accountNumber, details.accountDigit)}
            onChange={e => {
              const { number, digit } = splitDv(e.target.value);
              onChangeMany({ accountNumber: number, accountDigit: digit });
            }}
          />
        </div>
      </div>
      <div className="tdw-field">
        <label className="tdw-label">Tipo de conta</label>
        <div className="tdw-select-wrap">
          <select
            className="tdw-input"
            value={details.operationType || ''}
            onChange={e => onChange('operationType', e.target.value)}
          >
            <option value="">Selecionar tipo</option>
            {OPERATION_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span className="tdw-select-arrow"><Icon name="chevronDown" size={16} /></span>
        </div>
      </div>
    </div>
  );
}

// Account/agency "<number>-<digit>" mask: the last char is the verifier digit.
function splitDv(value) {
  const clean = String(value || '').toUpperCase().replace(/[^0-9X]/g, '');
  if (clean.length <= 1) return { number: clean, digit: '' };
  return { number: clean.slice(0, -1), digit: clean.slice(-1) };
}

function joinDv(number, digit) {
  if (!number && !digit) return '';
  return digit ? `${number || ''}-${digit}` : (number || '');
}

function validatePaymentDetails(method, details) {
  if (method === 'CreditCard') return validateCard(details);

  if (method === 'DebitAccount') {
    const error = validateOwnerDocument(details.ownerDocument);
    return error ? { ownerDocument: error } : {};
  }

  return {};
}

function validatePaymentField(method, field, details) {
  if (field === 'ownerDocument') return validateOwnerDocument(details.ownerDocument);
  if (method === 'CreditCard') return validateCardField(field, details);
  return null;
}

function validateOwnerDocument(value) {
  const document = String(value || '').trim();
  if (!document) return 'CPF/CNPJ do titular é obrigatório';
  if (!isValidDocument(document)) return 'CPF/CNPJ inválido';
  return null;
}

function isPaymentValid(method, details) {
  if (!method) return false;
  if (method === 'CreditCard') {
    return !!(details.cardNumber && details.cardBrand && details.cardName && details.expiry && details.cvv);
  }
  if (method === 'DebitAccount') {
    return !!(details.gateway && details.ownerName && details.ownerDocument
      && details.agency && details.accountNumber && details.accountDigit && details.operationType);
  }
  return true; // Pix, Boleto
}


function formatExpiry(value) {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
}
